import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Copy, Download, ShieldCheck } from "lucide-react";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { OtpInput } from "@/components/auth/OtpInput";
import { Spinner } from "@/components/common/FullScreenLoader";
import { homeFor, useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

function CopyButton({ value, label, testId }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Clipboard unavailable — copy manually.");
    }
  }
  return (
    <button type="button" onClick={copy} data-testid={testId} className="bz-btn-ghost text-xs">
      {copied ? <Check className="h-3.5 w-3.5 text-electric" strokeWidth={2} /> : <Copy className="h-3.5 w-3.5" strokeWidth={1.75} />}
      {copied ? "Copied" : label}
    </button>
  );
}

export default function MfaSetup() {
  const navigate = useNavigate();
  const { user, session, refresh } = useAuth();
  const [enrolment, setEnrolment] = useState(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const required = user?.user_type === "staff";

  async function start() {
    setError(null);
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enrol/start");
      setEnrolment(data);
    } catch (e) {
      setError(apiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  async function confirm(value) {
    setError(null);
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enrol/confirm", { code: value });
      setRecoveryCodes(data.recovery_codes);
      toast.success("Multi-factor authentication enabled");
    } catch (e) {
      const err = apiError(e);
      setError(err.code === "rate_limited" ? `Too many attempts. Wait ${err.retryAfter || 60}s.` : err.message);
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    const data = await refresh();
    navigate(homeFor(data?.user || user), { replace: true });
  }

  if (recoveryCodes) {
    return (
      <AuthLayout eyebrow="Step 3 of 3" title="Save your recovery codes." subtitle="Each code signs you in once if you lose your authenticator. They will not be shown again.">
        <div className="space-y-6">
          <div data-testid="mfa-recovery-codes-container" className="grid grid-cols-2 gap-2 rounded-md border border-electric/20 bg-graphite-950 p-4">
            {recoveryCodes.map((rc, index) => (
              <span key={rc} data-testid={`mfa-recovery-code-${index}`} className="font-mono text-sm tracking-widest text-electric">
                {rc}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <CopyButton value={recoveryCodes.join("\n")} label="Copy all" testId="mfa-recovery-copy-button" />
            <a
              href={`data:text/plain;charset=utf-8,${encodeURIComponent(`BIZII recovery codes for ${user?.email}\n\n${recoveryCodes.join("\n")}\n`)}`}
              download="bizii-recovery-codes.txt"
              data-testid="mfa-recovery-download-link"
              className="bz-btn-ghost text-xs"
            >
              <Download className="h-3.5 w-3.5" strokeWidth={1.75} /> Download .txt
            </a>
          </div>
          <button type="button" onClick={finish} data-testid="mfa-enrol-continue-button" className="bz-btn-primary w-full">
            <ShieldCheck className="h-4 w-4" strokeWidth={2} /> I have saved these — continue
          </button>
        </div>
      </AuthLayout>
    );
  }

  if (!enrolment) {
    return (
      <AuthLayout
        eyebrow={required ? "Required for staff" : "Optional security"}
        title="Set up your authenticator."
        subtitle={
          required
            ? `${user?.email} is a BIZII staff identity. Multi-factor authentication must be enabled before you can access the hub.`
            : "Add a second factor with any TOTP authenticator app (1Password, Google Authenticator, Authy)."
        }
        footer={
          session?.state === "mfa_enrolment_required" ? (
            <p data-testid="mfa-enrol-window-note">Your sign-in is held for 15 minutes while you enrol.</p>
          ) : null
        }
      >
        <div className="space-y-6">
          <ol className="space-y-3 text-sm text-slate-300">
            {["Install an authenticator app on your phone", "Scan the QR code we generate", "Enter the 6-digit code to confirm", "Store your recovery codes safely"].map((step, index) => (
              <li key={step} className="flex items-start gap-3">
                <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full border border-white/15 font-mono text-[11px] text-slate-400">{index + 1}</span>
                {step}
              </li>
            ))}
          </ol>
          {error ? (
            <div role="alert" data-testid="mfa-enrol-error" className="bz-alert-error">
              {error}
            </div>
          ) : null}
          <button type="button" onClick={start} disabled={busy} data-testid="mfa-enrol-start-button" className="bz-btn-primary w-full">
            {busy ? <Spinner /> : null}
            Generate my QR code
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout eyebrow="Step 2 of 3" title="Scan, then confirm." subtitle="Scan with your authenticator app, then enter the current 6-digit code.">
      <div className="space-y-6">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
          <div className="flex-none rounded-lg bg-white p-2 shadow-[0_0_30px_rgba(0,242,253,0.15)]">
            <img src={enrolment.qr_data_uri} alt="Authenticator enrolment QR code" width={168} height={168} data-testid="mfa-enrol-qr-image" className="h-[168px] w-[168px]" />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <p className="bz-caption">Can’t scan? Enter manually</p>
            <code data-testid="mfa-enrol-secret-text" className="block break-all rounded border border-white/10 bg-graphite-950 px-3 py-2 font-mono text-xs tracking-wider text-slate-200">
              {enrolment.manual_secret}
            </code>
            <CopyButton value={enrolment.manual_secret} label="Copy secret" testId="mfa-enrol-copy-secret-button" />
            <p className="text-xs text-slate-500">Time-based · 6 digits · 30s</p>
          </div>
        </div>
        <div className="space-y-3">
          <p className="bz-caption">Confirmation code</p>
          <OtpInput value={code} onChange={setCode} onComplete={confirm} disabled={busy} testId="mfa-enrol-code-input" autoFocus={false} />
        </div>
        {error ? (
          <div role="alert" data-testid="mfa-enrol-error" className="bz-alert-error">
            {error}
          </div>
        ) : null}
        <button type="button" onClick={() => confirm(code)} disabled={busy || code.length !== 6} data-testid="mfa-enrol-confirm-button" className="bz-btn-primary w-full">
          {busy ? <Spinner /> : null}
          Confirm and enable
        </button>
      </div>
    </AuthLayout>
  );
}
